import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart, useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, Loader2, Crown, ShoppingBag, Store } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";

const CheckoutCompletePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { fetchCart } = useCart();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [orderData, setOrderData] = useState(null);
  const [membershipActivated, setMembershipActivated] = useState(false);
  const [savings, setSavings] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyPayment = async () => {
      // Prevent duplicate calls (React Strict Mode / fast refresh)
      if (hasVerified.current) return;
      hasVerified.current = true;

      const accessCode = searchParams.get("AccessCode");
      if (!accessCode) {
        setStatus("error");
        setErrorMessage("Missing payment reference. Please try again.");
        return;
      }

      try {
        const response = await axios.post(`${API}/checkout/complete`, {
          access_code: accessCode,
        });

        if (response.data.success) {
          setStatus("success");
          setOrderData(response.data.order);
          setMembershipActivated(response.data.membership_activated || false);
          setSavings(response.data.savings || 0);
          await fetchCart();
          if (response.data.membership_activated && refreshUser) {
            await refreshUser();
          }
          toast.success(
            response.data.membership_activated
              ? "Order confirmed & membership activated!"
              : "Payment successful! Order confirmed."
          );
        } else {
          setStatus("failed");
          setErrorMessage(response.data.message || "Payment was declined.");
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(error.response?.data?.detail || "Unable to verify payment.");
      }
    };

    verifyPayment();
  }, [searchParams, fetchCart, refreshUser]);

  const groupedByStore = orderData?.items?.reduce((acc, item) => {
    const store = item.store_name || item.retailer || "Unknown";
    if (!acc[store]) acc[store] = [];
    acc[store].push(item);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12" data-testid="checkout-complete-page">
      <div className="bg-white rounded-xl shadow-sm p-8 max-w-lg w-full mx-4">
        {status === "verifying" && (
          <div className="text-center" data-testid="payment-verifying">
            <Loader2 className="w-12 h-12 text-sky-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Verifying Payment</h2>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="payment-success">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900">
                {membershipActivated ? "Order Confirmed & Membership Activated!" : "Order Confirmed!"}
              </h2>
              {membershipActivated && (
                <p className="text-emerald-700 text-sm mt-1">
                  Thank you for joining our community.{savings > 0 ? ` You saved $${savings.toFixed(2)} on this shop by upgrading.` : ""}
                </p>
              )}
              {orderData?.order_number && (
                <p className="text-sm text-slate-500 mt-2">Order: {orderData.order_number}</p>
              )}
            </div>

            {/* Order breakdown grouped by store */}
            {groupedByStore && Object.keys(groupedByStore).length > 0 && (
              <div className="mb-4 space-y-3">
                {Object.entries(groupedByStore).map(([storeName, items]) => (
                  <div key={storeName} className="bg-slate-50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Store className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-semibold text-slate-700">{storeName}</span>
                    </div>
                    {items.map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm pl-6">
                        <span className="text-slate-600">{item.quantity}x {item.product_name}</span>
                        <span className="font-medium">${(item.price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <Separator className="my-4" />

            {/* Fee breakdown */}
            {orderData && (
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Grocery Subtotal</span>
                  <span>${orderData.subtotal?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 flex items-center gap-1">
                    {orderData.is_member_order ? "4%" : "7%"} Convenience Fee
                    {orderData.is_member_order && <Crown className="w-3 h-3 text-sky-600" />}
                  </span>
                  <span>${orderData.convenience_fee?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Delivery</span>
                  <span>$10.00</span>
                </div>
                {orderData.membership_fee > 0 && (
                  <div className="flex justify-between text-emerald-700">
                    <span className="flex items-center gap-1"><Crown className="w-3 h-3" /> Membership</span>
                    <span>$5.00</span>
                  </div>
                )}
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Total Charged</span>
                  <span>${orderData.total?.toFixed(2)}</span>
                </div>
                {/* Fee Disclaimer */}
                <div className="mt-3 p-2 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>Fee Information:</strong> A convenience fee of 4% for Members or 7% for Guests applies, capped at $15 max. Delivery is a flat $10.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-3 mt-6">
              <Button className="w-full btn-accent" onClick={() => navigate(orderData ? `/orders/${orderData.order_number || orderData.id}` : "/orders")} data-testid="view-order-btn">
                <ShoppingBag className="w-4 h-4 mr-2" /> View Order
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/products")} data-testid="continue-shopping-btn">
                Continue Shopping
              </Button>
            </div>
          </div>
        )}

        {(status === "failed" || status === "error") && (
          <div className="text-center" data-testid="payment-failed">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">
              {status === "failed" ? "Payment Declined" : "Payment Error"}
            </h2>
            <p className="text-slate-600 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Button className="w-full btn-accent" onClick={() => navigate("/checkout")} data-testid="try-again-btn">Try Again</Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/cart")} data-testid="back-to-cart-btn">Back to Cart</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutCompletePage;
